import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Warning as AlertIcon,
  Medication as MedIcon,
  PriorityHigh as ProblemIcon,
  CalendarToday as RecallIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Event as EventIcon,
  ChevronRight as MoreIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { INSTANT_FORMATS, formatCalendarDate } from '../../../utils/dateUtils';
import { useFormatInstant } from '../../../hooks/useClinicTimezone';

export interface MedicalAlert {
  id: string;
  text: string;
  isCritical?: boolean;
}

interface ActiveMedication {
  id: string;
  name: string;
  dosage?: string;
  frequency?: string;
  isArchived?: boolean;
  endDate?: string;
}

interface ActiveProblem {
  id: string;
  title?: string;
  description?: string;
  problem?: string;
  status?: string;
}

interface ActiveRecall {
  id: string;
  title?: string;
  description?: string;
  reason?: string;
  type?: string;
  dueDate?: string;
}

export interface ContextRailProps {
  alerts: MedicalAlert[];
  activeMedications: ActiveMedication[];
  activeProblems: ActiveProblem[];
  activeRecalls: ActiveRecall[];
  lastVisitDate: Date | null;
  nextAppointmentDate: Date | null;
  canEdit: boolean;
  onEditAlerts: () => void;
  onShowActiveMeds: () => void;
  onShowProblems: () => void;
  onShowRecalls: () => void;
}

const SECTION_PAPER_SX = {
  p: 1.5,
  borderRadius: 2,
  boxShadow: 'none',
  border: '1px solid',
  borderColor: 'divider',
};

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  label: string;
  count?: number;
  color?: string;
  action?: React.ReactNode;
}> = ({ icon, label, count, color, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: color || 'text.primary', flexGrow: 1 }}>
      {icon}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      {count !== undefined && count > 0 && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {count}
        </Typography>
      )}
    </Box>
    {action}
  </Box>
);

/**
 * Always-visible left rail for the patient chart. Surfaces the
 * "I need this every visit" facts (allergies, active meds, problems,
 * recalls) so vets don't have to dig through tabs to find them.
 *
 * Each section degrades to a one-line "Add …" affordance when empty so the
 * rail still looks intentional on a brand-new patient.
 */
const ContextRail: React.FC<ContextRailProps> = ({
  alerts,
  activeMedications,
  activeProblems,
  activeRecalls,
  lastVisitDate,
  nextAppointmentDate,
  canEdit,
  onEditAlerts,
  onShowActiveMeds,
  onShowProblems,
  onShowRecalls,
}) => {
  // The rail shows both kinds: two INSTANTS (the visit summary, both
  // `Appointment.date`) and one CALENDAR day (`RecallReminder.dueDate`). Opposite
  // helpers on purpose — see the comments at each site.
  const formatInstant = useFormatInstant();
  const hasCriticalAlert = alerts.some((a) => a.isCritical);
  const meds = activeMedications.slice(0, 5);
  const problems = activeProblems.slice(0, 4);
  const recalls = activeRecalls.slice(0, 3);

  return (
    <Stack spacing={1.25} sx={{ width: '100%' }}>
      {/* Visit summary */}
      <Paper sx={SECTION_PAPER_SX}>
        <SectionHeader icon={<EventIcon sx={{ fontSize: 16 }} />} label="Visits" />
        <Stack spacing={0.5}>
          {/* INSTANT. Both dates in this block are `Appointment.date`, which
              `appointments.controller.ts:42-47` stores through a real zone-aware
              conversion (`DateTime.fromISO(date, { zone: timeZone }).toUTC()`) —
              DATE-KINDS.md §1.3, "Scheduling". A moment, so it renders in the CLINIC'S
              zone; the browser's is right only for someone standing in the clinic.
              These two were never caught by scripts/check-date-idioms.js because the
              caller already holds a Date (`format(dateObj)` does not match the regex) —
              same wrong-zone defect, no ratchet line. */}
          <Typography variant="caption" color="text.secondary">
            Last visit:{' '}
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {lastVisitDate
                ? formatInstant(lastVisitDate, INSTANT_FORMATS.date)
                : 'No visits'}
            </Box>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Next appt:{' '}
            <Box
              component="span"
              sx={{
                color: nextAppointmentDate ? 'text.primary' : 'text.secondary',
                fontWeight: nextAppointmentDate ? 600 : 400,
              }}
            >
              {/* `formatDistanceToNow` stays: it diffs two instants, so it is
                  timezone-agnostic and has nothing to re-zone (DATE-KINDS §2.3). */}
              {nextAppointmentDate
                ? `${formatInstant(nextAppointmentDate, INSTANT_FORMATS.date)} (${formatDistanceToNow(nextAppointmentDate, { addSuffix: true })})`
                : 'None scheduled'}
            </Box>
          </Typography>
        </Stack>
      </Paper>

      {/* Alerts / allergies */}
      <Paper
        sx={{
          ...SECTION_PAPER_SX,
          borderColor: hasCriticalAlert ? 'error.main' : alerts.length > 0 ? 'warning.main' : 'divider',
          bgcolor: hasCriticalAlert ? 'rgba(244, 67, 54, 0.04)' : alerts.length > 0 ? 'rgba(255, 152, 0, 0.04)' : 'background.paper',
        }}
      >
        <SectionHeader
          icon={<AlertIcon sx={{ fontSize: 16 }} />}
          label="Alerts"
          color={hasCriticalAlert ? 'error.main' : alerts.length > 0 ? 'warning.main' : 'text.secondary'}
          count={alerts.length}
          action={
            canEdit && (
              <IconButton size="small" onClick={onEditAlerts} sx={{ p: 0.25 }} aria-label="manage alerts">
                {alerts.length === 0 ? <AddIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            )
          }
        />
        {alerts.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No active alerts
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {alerts.map((alert) => (
              <Chip
                key={alert.id}
                label={alert.text}
                size="small"
                color={alert.isCritical ? 'error' : 'warning'}
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            ))}
          </Stack>
        )}
      </Paper>

      {/* Active medications */}
      <Paper sx={SECTION_PAPER_SX}>
        <SectionHeader
          icon={<MedIcon sx={{ fontSize: 16 }} />}
          label="Active Meds"
          count={activeMedications.length}
          action={
            activeMedications.length > meds.length && (
              <Tooltip title="Show all medications">
                <IconButton size="small" onClick={onShowActiveMeds} sx={{ p: 0.25 }} aria-label="all medications">
                  <MoreIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )
          }
        />
        {meds.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No active medications
          </Typography>
        ) : (
          <Stack spacing={0.4}>
            {meds.map((med) => (
              <Box key={med.id} sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.82rem',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={`${med.name}${med.dosage ? ' ' + med.dosage : ''}${med.frequency ? ' ' + med.frequency : ''}`}
                >
                  {med.name}
                </Typography>
                {(med.dosage || med.frequency) && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                    }}
                  >
                    {[med.dosage, med.frequency].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Problem list */}
      <Paper sx={SECTION_PAPER_SX}>
        <SectionHeader
          icon={<ProblemIcon sx={{ fontSize: 16 }} />}
          label="Active Problems"
          count={activeProblems.length}
          action={
            activeProblems.length > problems.length && (
              <Tooltip title="Show full problem list">
                <IconButton size="small" onClick={onShowProblems} sx={{ p: 0.25 }} aria-label="full problem list">
                  <MoreIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )
          }
        />
        {problems.length === 0 ? (
          <Button
            size="small"
            onClick={onShowProblems}
            sx={{ fontSize: '0.7rem', textTransform: 'none', p: 0, justifyContent: 'flex-start' }}
          >
            Add problem
          </Button>
        ) : (
          <Stack spacing={0.4}>
            {problems.map((p) => (
              <Typography
                key={p.id}
                variant="body2"
                sx={{
                  fontSize: '0.82rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={p.title || p.description || p.problem}
              >
                {p.title || p.description || p.problem || 'Problem'}
              </Typography>
            ))}
          </Stack>
        )}
      </Paper>

      {/* Recall reminders */}
      <Paper sx={SECTION_PAPER_SX}>
        <SectionHeader
          icon={<RecallIcon sx={{ fontSize: 16 }} />}
          label="Recalls"
          count={activeRecalls.length}
          action={
            activeRecalls.length > recalls.length && (
              <Tooltip title="Show all recalls">
                <IconButton size="small" onClick={onShowRecalls} sx={{ p: 0.25 }} aria-label="all recalls">
                  <MoreIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )
          }
        />
        {recalls.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No upcoming recalls
          </Typography>
        ) : (
          <Stack spacing={0.4}>
            {recalls.map((r) => (
              <Box key={r.id} sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.82rem',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={r.title || r.description || r.reason || r.type}
                >
                  {r.title || r.description || r.reason || r.type || 'Recall'}
                </Typography>
                {r.dueDate && (
                  /* CALENDAR — `RecallReminder.dueDate` at UTC midnight (DATE-KINDS.md
                     §1.1; burndown §2.2), the same column RecallRemindersPanel and
                     DueListTable render. `format(new Date(iso))` re-read it in the
                     VIEWER'S zone, so this rail showed the PREVIOUS day for every US
                     clinic. Parsed as a date PART, it is the same string in every zone —
                     asserted across all three browser projects in
                     e2e/specs/chart-panels.spec.ts. NOT the instant helper two blocks
                     up: re-zoning a calendar day is the INVERSE trap. */
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
                    data-testid="context-rail-recall-due"
                  >
                    {formatCalendarDate(r.dueDate, 'MMM d, yyyy')}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

export default ContextRail;
